import { reactive, computed } from 'vue';
import { defineStore } from 'pinia';
/**
 * 토스트 메시지 관리 스토어
 * 여러 토스트 메시지를 동시에 표시하고 독립적으로 관리하기 위한 상태 관리
 */
export const useToastStore = defineStore('toast', () => {
    /**
     * 활성화된 토스트 메시지 목록 (내부 상태)
     */
    const activeToasts = reactive([]);
    /**
     * 토스트 ID 카운터
     */
    let toastCounter = 0;
    /**
     * 현재 표시할 토스트 메시지 목록 (읽기 전용)
     */
    const toasts = computed(() => activeToasts);
    function addToast(message, optionsOrDuration) {
        const id = `toast-${++toastCounter}`;
        if (typeof optionsOrDuration === 'number') {
            activeToasts.push({
                id,
                message,
                duration: optionsOrDuration,
                show: true,
            });
            return;
        }
        // 옵션이 객체이거나 undefined인 경우
        activeToasts.push({
            id,
            title: optionsOrDuration?.title,
            message,
            details: optionsOrDuration?.details,
            duration: optionsOrDuration?.duration ?? 1200,
            show: true,
        });
    }
    /**
     * 특정 토스트 메시지를 목록에서 제거
     * @param id - 제거할 토스트의 ID
     */
    const removeToast = (id) => {
        const index = activeToasts.findIndex((toast) => toast.id === id);
        if (index !== -1) {
            activeToasts.splice(index, 1);
        }
    };
    /**
     * 모든 토스트 메시지 초기화
     */
    const clearAllToasts = () => {
        activeToasts.splice(0, activeToasts.length);
    };
    /**
     * 활성화된 Bottom Toast 목록 (내부 상태)
     */
    const activeBottomToasts = reactive([]);
    /**
     * Bottom Toast 타입을 UI variant + icon으로 매핑
     * - order-rejection -> error, warning2
     * - order-triggered -> info-purple, notification
     * - order-complete / order-execution / order-info -> info, info
     */
    const getStyleByType = (type) => {
        switch (type) {
            case 'order-rejection':
                return { variant: 'error', icon: 'warning2' };
            case 'order-triggered':
                return { variant: 'info-purple', icon: 'warning' };
            case 'order-complete':
                return { variant: 'info', icon: 'check-sm' };
            case 'order-execution':
                return { variant: 'info-blue', icon: 'check-sm' };
            case 'order-modified':
                return { variant: 'warning', icon: 'check-sm' };
            case 'order-cancelled':
                return { variant: 'error', icon: 'close small' };
            case 'order-info':
            default:
                return { variant: 'info', icon: 'check-sm' };
        }
    };
    /**
     * Bottom Toast ID 카운터
     */
    let bottomToastCounter = 0;
    /**
     * 먼저 온 순서대로 최대 3개만 표시
     * 먼저 온 토스트가 (닫기 또는 3초 후) 사라져야 그다음 대기 중인 토스트가 올라옴
     */
    const activeBottomToast = computed(() => {
        return [...activeBottomToasts]
            .sort((a, b) => a.timestamp - b.timestamp) // 먼저 온 것 우선
            .slice(0, 3);
    });
    /**
     * Bottom Toast 추가
     * @param type - BottomToastType (order-complete, order-execution, ...)
     * @param options - 토스트 내용 (message 필수, title 옵션)
     */
    function addBottomToast(type, options) {
        const id = `bottom-toast-${++bottomToastCounter}`;
        const { variant, icon } = getStyleByType(type);
        activeBottomToasts.push({
            id,
            type,
            title: options.title,
            message: options.message,
            timestamp: Date.now(),
            variant,
            icon,
        });
    }
    /**
     * Bottom Toast 제거
     */
    const removeBottomToast = (id) => {
        const index = activeBottomToasts.findIndex((toast) => toast.id === id);
        if (index !== -1) {
            activeBottomToasts.splice(index, 1);
        }
    };
    /**
     * 모든 Bottom Toast 초기화
     */
    const clearAllBottomToasts = () => {
        activeBottomToasts.splice(0, activeBottomToasts.length);
    };
    /**
     * 주문 완료 토스트 추가
     */
    const addOrderCompleteToast = (orderType, message) => {
        addBottomToast('order-complete', { title: `${orderType} 주문 완료`, message });
    };
    /**
     * 체결 완료 토스트 추가
     */
    const addExecutionCompleteToast = (message) => {
        addBottomToast('order-execution', { title: '체결', message });
    };
    /**
     * 주문 발동 토스트 추가
     */
    const addOrderTriggeredToast = (message) => {
        addBottomToast('order-triggered', { title: '주문 발동', message });
    };
    /**
     * 주문 거부 토스트 추가
     */
    const addOrderRejectedToast = (message) => {
        addBottomToast('order-rejection', { title: '주문 거부', message });
    };
    const addOrderModifiedToast = (message) => {
        addBottomToast('order-modified', { title: '정정 완료', message });
    };
    const addOrderCancelledToast = (message) => {
        addBottomToast('order-cancelled', { title: '취소 완료', message });
    };
    return {
        toasts,
        addToast,
        removeToast,
        clearAllToasts,
        activeBottomToast,
        addBottomToast,
        removeBottomToast,
        clearAllBottomToasts,
        addOrderCompleteToast,
        addExecutionCompleteToast,
        addOrderTriggeredToast,
        addOrderRejectedToast,
        addOrderModifiedToast,
        addOrderCancelledToast,
    };
});
//# sourceMappingURL=useToastStore.js.map